/**
 * The rules for stepping through the open requests. They are plain functions of numbers and flags,
 * so the component holds no rule of its own and a test can run them without a browser.
 */

/** How far a finger or pen must travel across the banner for a swipe, in CSS pixels. Exceeded, not met. */
export const SWIPE_DISTANCE_PX = 40;

/**
 * The step after moving `delta` requests from `step`, wrapping at both ends. Steps count from 1.
 * A `step` outside 1 to `total` counts as the nearest request, as it does for the dots, so the
 * control steps from the request it draws as current.
 */
export function wrapStep(step: number, delta: number, total: number): number {
  const current = Math.min(Math.max(step, 1), total);
  const zeroBased = current - 1 + delta;
  return (((zeroBased % total) + total) % total) + 1;
}

type SwipeInput = {
  /** Distance travelled across the screen, positive to the right. */
  dx: number;
  /** Distance travelled down the screen. */
  dy: number;
  /** The pointer that made the swipe: "touch", "pen" or "mouse". */
  pointerType: string;
};

const SWIPE_POINTERS = ['touch', 'pen'];

/**
 * +1 (next request) for a swipe left, -1 (previous request) for a swipe right, 0 for anything
 * else. A mouse never swipes: it has the buttons and the arrow keys. A drag that is more up or
 * down than across is a page scroll, not a swipe.
 */
export function swipeStepDelta({ dx, dy, pointerType }: SwipeInput): -1 | 0 | 1 {
  const isSwipePointer = SWIPE_POINTERS.includes(pointerType);
  const isFarEnough = Math.abs(dx) > SWIPE_DISTANCE_PX;
  const isMostlyAcross = Math.abs(dx) > Math.abs(dy);
  if (!isSwipePointer || !isFarEnough || !isMostlyAcross) {
    return 0;
  }
  return dx < 0 ? 1 : -1;
}

type KeyInput = {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  /** Something nearer the key press already acted on it. */
  defaultPrevented: boolean;
  /** The focused control uses the arrow keys itself, such as a text field. */
  targetHandlesArrows: boolean;
};

const KEY_DELTAS: Record<string, -1 | 1> = { ArrowLeft: -1, ArrowRight: 1 };

/**
 * +1 for the right arrow, -1 for the left arrow, 0 for any other key, for an arrow key pressed
 * with a modifier (the browser keeps those for itself), and for an arrow key something else has
 * already handled or the focused control needs.
 */
export function keyboardStepDelta(input: KeyInput): -1 | 0 | 1 {
  const hasModifier = [input.altKey, input.ctrlKey, input.metaKey, input.shiftKey].some(Boolean);
  const isTaken = input.defaultPrevented || input.targetHandlesArrows;
  if (hasModifier || isTaken) {
    return 0;
  }
  return KEY_DELTAS[input.key] ?? 0;
}
